import { z } from "zod";
import { scheduleApplicationStatusSchema } from "./domains/accounting/schedule.schema";
import { matchedScheduleSchema } from "./domains/accounting/scheduleMatchedDisplay.schema";
import { Taxonomy } from "@common/categories";
import { SortOrder } from "@common/constants";
import { TaxQuestionKey } from "@common/taxSavingRules";
import { CounterpartySource } from "@common/types/counterparty";
import { EntityIndustry, EntityProfileType } from "@common/types/entity";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import { RuleActionType } from "@common/types/transactionRule";
import { filtersSchema } from "@common/types/filters/general";
import { TransactionType, TransactionStatus } from "@common/types/transaction";
import { BankCashItemType } from "@common/types/bankCashItem";
import { InvoiceStatus } from "@common/types/invoice";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { isLikelyBase64 } from "@common/validators/is-likely-base64";
import {
    transactionFieldReviewSchema,
    transactionReviewFieldEnum,
} from "./transactionFieldReview.schema";
import { classAssignmentSchema } from "./class.schema";
import { transactionMatchSchema } from "./transactionMatch.schema";
import { accrualMatchDisplaySchema } from "./domains/accrualReview/accrual-review.schema";
import { accrualMatchSuggestionDisplaySchema } from "./domains/accrualReview/accrual-match-suggestion-display.schema";
import {
    accountOverridesRequestSchema,
    accountOverridesResponseSchema,
} from "./domains/accounting/account-override.schema";
import { dbIntIdSchema } from "./shared.schema";

export const BulkUnsplitPayloadSchema = z.object({
    transactionIds: z.array(z.number().nonnegative()).min(1),
});

const compactTransactionResponseSchema = z.object({
    id: z.number(),
});

export const getSimilarTransactionsResponseSchema = z.object({
    ids: z.array(z.number()),
});

export const matchTransferBodySchema = z.object({
    targetTransactionId: z.coerce.number().int().positive(),
});

export const matchTransferResponseSchema = z.object({
    originTransaction: compactTransactionResponseSchema,
    targetTransaction: compactTransactionResponseSchema,
    // True when the pair was already matched and the request was a no-op, so
    // callers (e.g. the MCP tool) can tell an idempotent replay from a fresh
    // write instead of both looking identical.
    alreadyMatched: z.boolean(),
});

export const unmatchTransferResponseSchema = z.object({
    transaction: compactTransactionResponseSchema,
});

export const linkFinancialTransactionDocumentBodySchema = z.object({
    financialDocumentId: z.coerce.number().int().positive(),
    financialTransactionId: z.coerce.number().int().positive(),
});

export const detachDocumentMatchResponseSchema = z.void();

export const updateTransactionSplitV2Schema = z
    .object({
        id: z
            .number()
            .describe(
                "Existing split line id when updating an existing split; omit this field for a new split line.",
            )
            .optional(),
        entityId: z
            .number()
            .describe(
                "Numeric Kick entity id that owns this split line. Use an entity lookup before splitting when only an entity name is known.",
            ),
        amount: z
            .number()
            .multipleOf(0.01)
            .describe(
                "Dollar amount (for example 80.00 for eighty dollars) assigned to this split line, precise to two decimal places.",
            ),
        splitDescription: z
            .string()
            .min(1)
            .describe(
                "Non-empty description for this split line, visible in bookkeeping review.",
            ),
        categoryId: z
            .string()
            .uuid()
            .describe(
                "Kick category UUIDv7 for this split line. Optional when a GL account is supplied via accountOverrides/accountId. Use category lookup before splitting when only a category name is known.",
            )
            .optional(),
        classIds: z
            .array(z.string().uuid())
            .describe(
                "Kick class UUIDv7 values to assign to this split line. Use class lookup before splitting when only class names are known.",
            )
            .optional(),
        counterpartyId: z
            .string()
            .uuid()
            .describe(
                "Kick counterparty UUIDv7 to assign to this split line. Use counterparty lookup before splitting when only a counterparty name is known.",
            )
            .optional(),
        accountOverrides: accountOverridesRequestSchema
            .describe(
                "Per-ledger Kick account override entries for this split line. Use account lookup before splitting when only an account name is known.",
            )
            .optional(),
        accountId: z
            .string()
            .uuid()
            .nullable()
            .describe(
                "Legacy primary cash ledger Kick account UUIDv7 for this split line, or null when no account should be assigned. Prefer accountOverrides for new clients.",
            )
            .optional(),
    })
    .refine(
        ({ categoryId, accountOverrides, accountId }) =>
            categoryId !== undefined ||
            accountOverrides !== undefined ||
            accountId !== undefined,
        {
            message:
                "Each split line must provide at least one of categoryId, accountOverrides, or accountId. " +
                "Splitting across entities does not exempt a line: repeat the same classification on every " +
                "line you do not want to re-categorize — categoryId in a category-first workspace, or " +
                "accountOverrides in a GL-first workspace.",
        },
    );

// Lightweight read used by the MCP/agent layer to resolve a transaction's
// owning workspace and whether it operates in GL-first mode, so the agent
// classification guard can react without fetching the full transaction payload.
export const transactionMcpContextResponseSchema = z.object({
    transactionId: z.number().int(),
    workspaceId: z.string().uuid(),
    glFirstEnabled: z.boolean(),
});

export const updateTransactionSplitsV2BodySchema = z.object({
    splits: z
        .array(updateTransactionSplitV2Schema)
        .min(1)
        .max(100)
        .describe(
            "Replacement split lines for the transaction. Provide at least one split, and ensure the split amounts sum to the original transaction amount before confirming the preview.",
        ),
});

const MAX_RECEIPT_BASE64_BYTES = 10 * 1024 * 1024;

const ALLOWED_RECEIPT_CONTENT_TYPES = [
    "image/jpeg",
    "image/png",
    "application/pdf",
] as const;

export const uploadReceiptBodySchema = z.object({
    receipt: z.string().max(MAX_RECEIPT_BASE64_BYTES).refine(isLikelyBase64, {
        message: "receipt must be a valid base64 string",
    }),
    contentType: z.enum(ALLOWED_RECEIPT_CONTENT_TYPES),
    name: z.string().min(1),
});

export const updateTransactionRequestSchema = z.object({
    memo: z.string().optional(),
    categoryId: z.string().uuid().optional(),
    categoryConfirmed: z.boolean().optional(),
    deductionRate: z.number().min(0).max(1).optional(),
    isIgnored: z.boolean().optional(),
    excludeFrom1099: z.boolean().optional(),
    entityId: dbIntIdSchema.optional(),
    requirementsSnoozed: z.boolean().optional(),
    loosingAccessConfirmed: z.boolean().optional(),
    taxQuestionAnswers: z
        .array(
            z.object({
                key: z.nativeEnum(TaxQuestionKey),
                answer: z.string(),
            }),
        )
        .optional(),
    classIds: z.array(z.string().uuid()).optional(),
    counterpartyId: z.string().uuid().nullable().optional(),
    customerId: z.string().uuid().nullable().optional(),
    accountOverrides: accountOverridesRequestSchema.optional(),
    // Transitional legacy fields. New callers should use accountOverrides.
    accountId: z.string().uuid().nullable().optional(),
    accrualAccountId: z.string().uuid().nullable().optional(),
});
export type UpdateTransactionRequest = z.infer<
    typeof updateTransactionRequestSchema
>;
export type UpdateTransactionRequestInput = z.input<
    typeof updateTransactionRequestSchema
>;

export const bulkUpdatePayloadSchema = z.object({
    categoryId: z.string().uuid().optional(),
    entityId: dbIntIdSchema.optional(),
    memo: z.string().optional(),
    excludeFrom1099: z.boolean().optional(),
    taxQuestionAnswers: z
        .array(
            z.object({
                key: z.nativeEnum(TaxQuestionKey),
                answer: z.string(),
            }),
        )
        .optional(),
    classes: z.array(z.string().uuid()).optional(),
    classIds: z.array(z.string().uuid()).optional(),
    counterpartyId: z.string().uuid().nullable().optional(),
    customerId: z.string().uuid().nullable().optional(),
    pairedTransferId: z.coerce.number().nullable().optional(),
    accountOverrides: accountOverridesRequestSchema.optional(),
    // Transitional: bulk-update UI applies a single payload to many
    // transactions spanning different entities; clients typically don't
    // have the per-entity primary-ledger id pre-resolved. Allow the
    // legacy account-id shape on the bulk payload and let the backend
    // infer the primary ledger per item. The single-update DTO has
    // already dropped these.
    accountId: z.string().uuid().nullable().optional(),
    accrualAccountId: z.string().uuid().nullable().optional(),
    ruleApplications: z
        .array(
            z.object({
                ruleId: z.string().uuid(),
                actionType: z.nativeEnum(RuleActionType),
            }),
        )
        .optional(),
});
export type BulkUpdatePayload = z.infer<typeof bulkUpdatePayloadSchema>;
export type BulkUpdatePayloadInput = z.input<typeof bulkUpdatePayloadSchema>;
export type BulkUpdateRuleApplicationInput = NonNullable<
    BulkUpdatePayloadInput["ruleApplications"]
>[number];

export const bulkUpdateTransactionWithPayloadSchema =
    bulkUpdatePayloadSchema.extend({
        transactionId: dbIntIdSchema,
    });
export type BulkUpdateTransactionWithPayload = z.infer<
    typeof bulkUpdateTransactionWithPayloadSchema
>;

// Built from `filtersSchema.shape` field-by-field instead of `.pick(...)` so
// the transaction contract does not serialize the full filters god-object
// (including unrelated page filters) into every route (TS7056).
const transactionFilterShape = filtersSchema.shape;

export const transactionFiltersSchema = z
    .object({
        search: transactionFilterShape.search,
        start: transactionFilterShape.start,
        end: transactionFilterShape.end,
        startDate: z.string().date().optional(),
        endDate: z.string().date().optional(),
        minAmount: transactionFilterShape.minAmount,
        maxAmount: transactionFilterShape.maxAmount,
        direction: transactionFilterShape.direction,
        amountRounded: transactionFilterShape.amountRounded,
        amountRoundingLevel: transactionFilterShape.amountRoundingLevel,
        category: transactionFilterShape.category,
        requiredAction: transactionFilterShape.requiredAction,
        requiredActions: transactionFilterShape.requiredActions,
        hasTransferMatch: transactionFilterShape.hasTransferMatch,
        transactionRulesIds: transactionFilterShape.transactionRulesIds,
        statuses: transactionFilterShape.statuses,
        classes: transactionFilterShape.classes,
        counterpartiesIds: transactionFilterShape.counterpartiesIds,
        withEmptyOrUnverifiedCounterparty:
            transactionFilterShape.withEmptyOrUnverifiedCounterparty,
        hasCounterparty: transactionFilterShape.hasCounterparty,
        customerIds: transactionFilterShape.customerIds,
        withEmptyCustomer: transactionFilterShape.withEmptyCustomer,
        hasCustomer: transactionFilterShape.hasCustomer,
        billableExpenseInvoiceStatuses:
            transactionFilterShape.billableExpenseInvoiceStatuses,
        matchTypes: transactionFilterShape.matchTypes,
        isSplitted: transactionFilterShape.isSplitted,
        isNotSplitted: transactionFilterShape.isNotSplitted,
        isReconciled: transactionFilterShape.isReconciled,
        isNotReconciled: transactionFilterShape.isNotReconciled,
        isUnreconciledCheck: transactionFilterShape.isUnreconciledCheck,
        transactionTypes: transactionFilterShape.transactionTypes,
        hasReceipt: transactionFilterShape.hasReceipt,
        noReceipt: transactionFilterShape.noReceipt,
        requiresReceipt: transactionFilterShape.requiresReceipt,
        hasNote: transactionFilterShape.hasNote,
        noNote: transactionFilterShape.noNote,
        requiresNote: transactionFilterShape.requiresNote,
        entityIds: transactionFilterShape.entityIds,
        accountIds: transactionFilterShape.accountIds,
        subaccountIds: transactionFilterShape.subaccountIds,
        classFilterType: transactionFilterShape.classFilterType,
        entityId: transactionFilterShape.entityId,
        accountCodes: transactionFilterShape.accountCodes,
        accountStatus: transactionFilterShape.accountStatus,
        transactionLocked: transactionFilterShape.transactionLocked,
        accountSource: transactionFilterShape.accountSource,
        coaAccounts: transactionFilterShape.coaAccounts,
        coaFilterType: transactionFilterShape.coaFilterType,
        accrualCoaAccounts: transactionFilterShape.accrualCoaAccounts,
        includeUnassignedAccrualAccount:
            transactionFilterShape.includeUnassignedAccrualAccount,
        transactionIds: transactionFilterShape.transactionIds,
        excludeSplits: transactionFilterShape.excludeSplits,
        includeBeforeBookkeepingStart:
            transactionFilterShape.includeBeforeBookkeepingStart,
        excludedTransactionIds: transactionFilterShape.excludedTransactionIds,
        normalizedDescriptionId: transactionFilterShape.normalizedDescriptionId,
        normalizedDescriptionIds:
            transactionFilterShape.normalizedDescriptionIds,
        accrualAccountId: transactionFilterShape.accrualAccountId,
        isAccrualReviewed: transactionFilterShape.isAccrualReviewed,
        isNotAccrualReviewed: transactionFilterShape.isNotAccrualReviewed,
        isNotAccrualApproved: transactionFilterShape.isNotAccrualApproved,
        accrualSuggestionBuckets:
            transactionFilterShape.accrualSuggestionBuckets,
        isScheduleSuspicious: transactionFilterShape.isScheduleSuspicious,
        hasScheduleDraft: transactionFilterShape.hasScheduleDraft,
        excludeLiveScheduled: transactionFilterShape.excludeLiveScheduled,
        auditLogFilter: transactionFilterShape.auditLogFilter,
        accountSubtypes: transactionFilterShape.accountSubtypes,
        cashGeneralLedgerRoles: transactionFilterShape.cashGeneralLedgerRoles,
        cashGeneralLedgerTypes: transactionFilterShape.cashGeneralLedgerTypes,
        accrualGeneralLedgerRoles:
            transactionFilterShape.accrualGeneralLedgerRoles,
        accrualGeneralLedgerTypes:
            transactionFilterShape.accrualGeneralLedgerTypes,
        reviewedFields: transactionFilterShape.reviewedFields,
        reviewedFieldsMode: transactionFilterShape.reviewedFieldsMode,
        unreviewedFields: transactionFilterShape.unreviewedFields,
        unreviewedFieldsMode: transactionFilterShape.unreviewedFieldsMode,
        reviewerIds: transactionFilterShape.reviewerIds,
        reviewerMode: transactionFilterShape.reviewerMode,
        excludeFrom1099: transactionFilterShape.excludeFrom1099,
        accountClasses: transactionFilterShape.accountClasses,
    })
    .strict();

export type TransactionFilters = z.infer<typeof transactionFiltersSchema>;

export const transactionExportColumnSchema = z.nativeEnum(TransactionColumn);
export type TransactionExportColumn = z.infer<
    typeof transactionExportColumnSchema
>;

export const bulkFieldReviewOptionsSchema = z.object({
    page: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    filters: transactionFiltersSchema,
    workspaceId: z.string().uuid(),
});

export const bulkFieldReviewRequestSchema = z
    .object({
        fields: z.array(transactionReviewFieldEnum).min(1),
        transactionIds: z
            .array(z.coerce.number().int().positive())
            .min(1)
            .max(500)
            .optional(),
        options: bulkFieldReviewOptionsSchema.optional(),
    })
    .refine(
        (data) =>
            Boolean(data.transactionIds ?? data.options) &&
            !(data.transactionIds && data.options),
        {
            message:
                "Exactly one of transactionIds or options must be provided, not both",
        },
    );
export type BulkFieldReviewRequest = z.infer<
    typeof bulkFieldReviewRequestSchema
>;

export const bulkUpdateTransactionOptionsSchema = z.object({
    page: z.coerce.number().int().min(0),
    limit: z.coerce.number().int().min(0),
    filters: transactionFiltersSchema,
});
export type BulkUpdateTransactionOptions = z.infer<
    typeof bulkUpdateTransactionOptionsSchema
>;

export const bulkUpdateTransactionsRequestSchema = z
    .object({
        transactions: z
            .array(bulkUpdateTransactionWithPayloadSchema)
            .min(1)
            .optional(),
        payload: bulkUpdatePayloadSchema.optional(),
        options: bulkUpdateTransactionOptionsSchema.optional(),
        processId: z.string().uuid().optional(),
        /**
         * When set, every transaction this bulk update actually changes is
         * auto-marked reviewed for this field — mirroring the manual
         * single-transaction review cells (CategoryCell, LedgerCell,
         * EntityCell, ClassesCell, NoteCell, CounterpartyCell), which already
         * auto-mark their field reviewed after a successful inline edit.
         * Callers only send this from a review-task context where the field
         * is an active review column (same gating as those cells' local
         * `isReviewVariant` check) — the backend applies it unconditionally
         * once set, using the exact same eligibility-filtered
         * `TransactionFieldReviewService.bulkAddReviews` mechanism the
         * bulk-field-review endpoints use, so empty/uncategorized/blank
         * values are never marked reviewed here either.
         */
        autoReviewField: transactionReviewFieldEnum.optional(),
    })
    .refine((data) => Boolean(data.transactions ?? data.payload), {
        message:
            "Either transactions or payload must be provided for bulk update",
    });
export type BulkUpdateTransactionsRequest = z.infer<
    typeof bulkUpdateTransactionsRequestSchema
>;
export type BulkUpdateTransactionsRequestInput = z.input<
    typeof bulkUpdateTransactionsRequestSchema
>;

export enum TransactionsQuerySortField {
    ID = "id",
    DATE = "date",
    AMOUNT = "amount",
    ORIGINAL_AMOUNT = "originalAmount",
    DISPLAY_DESCRIPTION = "displayDescription",
    BANK_DESCRIPTION = "bankDescription",
    CATEGORY = "category",
    FINANCIAL_ACCOUNT = "financialAccount",
    REQUIRED_ACTIONS_COUNT = "requiredActionsCount",
    SPLIT_PARENT_ID = "splitParentId",
    CLASS = "class",
    COUNTERPARTY = "counterparty",
    CUSTOMER = "customer",
    GENERAL_LEDGER = "generalLedger",
    REVIEW = "review",
    BILLABLE_EXPENSE_INVOICE_STATUS = "billableExpenseInvoiceStatus",
}

const transactionSortSchema = z.object({
    field: z.nativeEnum(TransactionsQuerySortField),
    order: z.nativeEnum(SortOrder),
});

export type TransactionSort = z.infer<typeof transactionSortSchema>;

export const transactionExportQuerySchema = transactionFiltersSchema.extend({
    columns: z.array(transactionExportColumnSchema).optional(),
    sort: transactionSortSchema.optional(),
});
export type TransactionExportQuery = z.infer<
    typeof transactionExportQuerySchema
>;

export const getTransactionsQuerySchema = z.object({
    page: z.coerce.number().int().default(1),
    limit: z.coerce.number().int().positive().max(100).default(20),
    sort: transactionSortSchema.optional().default({
        field: TransactionsQuerySortField.DATE,
        order: SortOrder.DESC,
    }),
    filters: transactionFiltersSchema.optional().default({}),
    workspaceId: z.string().uuid(),
    skipCount: z.coerce.boolean().optional().default(false),
    includeMatchDetails: z.coerce.boolean().optional().default(false),
    includeAccrualMatchDetails: z.coerce.boolean().optional().default(false),
    includeAccrualMatchSuggestions: z.coerce.boolean().optional(),
});

export const getTransactionsStatisticsQuerySchema = z.object({
    filters: transactionFiltersSchema.optional().default({}),
    workspaceId: z.string().uuid(),
});

export const getTransactionsSnapshotQuerySchema =
    getTransactionsQuerySchema.omit({ page: true, skipCount: true });

export type GetTransactionsQuery = z.infer<typeof getTransactionsQuerySchema>;
export type GetTransactionsSnapshotQuery = z.infer<
    typeof getTransactionsSnapshotQuerySchema
>;
export type GetTransactionsStatisticsQuery = z.infer<
    typeof getTransactionsStatisticsQuerySchema
>;

// Query for the "does this customer have unbilled billable expenses" check
// (PRO invoices UI): a transaction counts as unbilled when it carries the
// given customerId but has not yet been attached to a native invoice via
// billableExpenseInvoiceId.
export const hasUnbilledCustomerExpensesQuerySchema = z.object({
    customerId: z.string().uuid(),
    workspaceId: z.string().uuid(),
});
export type HasUnbilledCustomerExpensesQuery = z.infer<
    typeof hasUnbilledCustomerExpensesQuerySchema
>;

export const hasUnbilledCustomerExpensesResponseSchema = z.object({
    hasUnbilledExpenses: z.boolean(),
});
export type HasUnbilledCustomerExpensesResponse = z.infer<
    typeof hasUnbilledCustomerExpensesResponseSchema
>;

export const exportTransactionsQuerySchema = transactionFiltersSchema;

export const transactionsStatisticsResponseSchema = z.object({
    count: z.number(),
    cashIn: z.number(),
    cashOut: z.number(),
    total: z.number(),
    average: z.number(),
});

export type TransactionsStatistics = z.infer<
    typeof transactionsStatisticsResponseSchema
>;

// Transaction journal entry schema
const transactionJournalEntrySchema = z.object({
    id: z.string(),
    accountId: z.string(),
    lineOrder: z.number(),
    referenceNumber: z.string(),
    accountingLedger: z.object({
        id: z.string(),
        entityId: z.number(),
        basis: z.nativeEnum(AccountingLedgerBasis),
    }),
});

// Paired transfer schema
const pairedTransferSchema = z.object({
    id: z.number(),
    displayDescription: z.string().nullable(),
    /** @deprecated Use `dateOnly` for the accounting calendar day. */
    date: z.coerce.date(),
    dateOnly: z.string().nullable().optional(),
    amount: z.number(),
    financialAccountId: z.number().optional(),
    categoryId: z.string(),
    bankDescription: z.string(),
    entityId: z.number().optional(),
});

// Tax question answer schema
const taxQuestionAnswerSchema = z.object({
    id: z.number(),
    key: z.string(),
    answer: z.string(),
});

export const transactionScheduleSchema = z.object({
    scheduleApplicationId: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    // The accrual ledger the schedule governs; its account override on this
    // ledger is locked while the schedule is live.
    ledgerId: z.string().uuid(),
    scheduleName: z.string(),
    status: scheduleApplicationStatusSchema,
    // Last recognition period's date; null until periods exist (drafts).
    termEnd: z.string().date().nullable(),
    // Applied amount less recognition periods dated up to today, whether or
    // not posted; the Schedule column's "left".
    remainingAmount: z.number(),
});

export type TransactionSchedule = z.infer<typeof transactionScheduleSchema>;

// Minimal references carried on a transaction payload. Kept as standalone
// objects (rather than `someSchema.pick(...)`) so the transaction contract's
// inferred type does not serialize the full source schema shapes (TS7056).
const transactionAccountReferenceSchema = z.object({
    id: z.string(),
    name: z.string(),
    code: z.number(),
});

const transactionFinancialAccountReferenceSchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    sourceType: z.nativeEnum(FinancialAccountSourceType),
    accountNumberMask: z.string().nullable(),
    entityId: z.number().nullable(),
});

const transactionEntityReferenceSchema = z.object({
    federalTaxRate: z.number(),
    iconColor: z.string(),
    id: z.number(),
    industry: z.nativeEnum(EntityIndustry),
    isBusiness: z.boolean(),
    isPersonal: z.boolean(),
    legalType: z.nativeEnum(EntityProfileType).nullable(),
    name: z.string().nullable(),
    userId: z.number().nullable(),
    workspaceId: z.string(),
    logo: z.string().nullable(),
});

const transactionCategoryReferenceSchema = z.object({
    id: z.string(),
    label: z.string(),
    parentCategoryId: z.string().nullable(),
    taxonomy: z.nativeEnum(Taxonomy),
    workspaceId: z.string().nullable(),
});

const transactionCounterpartyReferenceSchema = z.object({
    id: z.string(),
    name: z.string(),
    shouldBeNormalized: z.boolean(),
    updatedByUserId: z.number().nullable(),
    source: z.nativeEnum(CounterpartySource).nullable(),
    workspaceId: z.string().nullable(),
});

const transactionSubaccountReferenceSchema = z.object({
    id: z.string(),
    name: z.string(),
    accountNumber: z.string().nullable(),
});

const transactionBillableExpenseInvoiceReferenceSchema = z.object({
    id: z.string(),
    status: z.nativeEnum(InvoiceStatus),
});

const transactionCashItemReferenceSchema = z.object({
    type: z.nativeEnum(BankCashItemType),
});

// Base transaction item schema (shared between parent and split children)
const transactionItemBaseSchema = z.object({
    id: z.number(),
    /**
     * Optional synthetic row key used when the same transaction is rendered
     * multiple times (e.g. split previews where a single transaction fans out
     * into one row per split part). Consumers should prefer this over `id`
     * for React keys / table row ids when it is set.
     */
    previewRowId: z.string().optional(),
    /** @deprecated Use `dateOnly` for the accounting calendar day. Legacy UTC timestamp; persist on writes only. */
    date: z.coerce.date(),
    dateOnly: z.string().nullable().optional(),
    amountSaved: z.number().nullable(),
    amount: z.number(),
    status: z.nativeEnum(TransactionStatus),
    splitParentId: z.number().nullable().optional(),
    counterpartyId: z.string().nullable(),
    customerId: z.string().nullable().optional(),
    billableExpenseInvoiceId: z.string().uuid().nullable().optional(),
    categoryId: z.string(),
    isIgnored: z.boolean(),
    isBusiness: z.boolean(),
    excludeFrom1099: z.boolean().optional(),
    type: z.nativeEnum(TransactionType),
    suggestedCategories: z.array(z.string()).nullable().optional(),
    requiredActions: z.array(z.string()),
    requirementsSnoozed: z.boolean(),
    potentialAmountSaved: z.number().nullable(),
    bankDescription: z.string(),
    splitDescription: z.string().nullable().optional(),
    originalAmount: z.number(),
    foreignCurrencyAmount: z.number().nullable().optional(),
    foreignCurrency: z.string().nullable().optional(),
    pairedTransferId: z.number().nullable().optional(),
    inReview: z.boolean(),
    memo: z.string().nullable(),
    displayDescription: z.string().nullable(),
    deductionRate: z.number(),
    businessCategoryId: z.string().nullable(),
    // Transitional: optional on the response schema while every
    // transaction-returning controller path is migrated to hydrate
    // `accountOverrides`. Phase 6 of the migration tightens this to
    // required and drops `accountId` / `accrualAccountId`.
    accountOverrides: accountOverridesResponseSchema.optional(),
    accountId: z.string().nullable(),
    accrualAccountId: z.string().nullable().optional(),
    suggestedAccrualAccountId: z.string().nullable().optional(),
    subaccountId: z.string().nullable().optional(),
    reconciledInId: z.string().nullable().optional(),
    categoryIdLastUpdatedAt: z.coerce.date().nullable().optional(),
    financialAccountId: z.number().optional(),
    requiredActionsCount: z.number().nullable().optional(),
    financialAccount: transactionFinancialAccountReferenceSchema,
    entity: transactionEntityReferenceSchema,
    category: transactionCategoryReferenceSchema.optional(),
    counterparty: transactionCounterpartyReferenceSchema.nullable().optional(),
    customer: transactionCounterpartyReferenceSchema.nullable().optional(),
    billableExpenseInvoice: transactionBillableExpenseInvoiceReferenceSchema
        .nullable()
        .optional(),
    classAssignments: z.array(classAssignmentSchema).optional(),
    transactionMatches: z.array(transactionMatchSchema).optional(),
    accrualMatches: z.array(accrualMatchDisplaySchema).optional(),
    accrualMatchSuggestions: z
        .array(accrualMatchSuggestionDisplaySchema)
        .optional(),
    taxQuestionAnswers: z
        .array(
            taxQuestionAnswerSchema.pick({
                id: true,
                answer: true,
            }),
        )
        .optional(),
    journalEntries: z.array(transactionJournalEntrySchema).optional(),
    pairedTransfer: pairedTransferSchema.nullable().optional(),
    subaccount: transactionSubaccountReferenceSchema.nullable().optional(),
    account: transactionAccountReferenceSchema.nullable().optional(),
    accrualAccount: transactionAccountReferenceSchema.nullable().optional(),
    suggestedAccrualAccount: transactionAccountReferenceSchema
        .nullable()
        .optional(),
    schedule: transactionScheduleSchema.optional(),
    // Only set when the transaction has no schedule of its own.
    matchedSchedules: z.array(matchedScheduleSchema).optional(),
    fieldReviews: z.array(transactionFieldReviewSchema).optional(),
    cashItem: transactionCashItemReferenceSchema.nullable().optional(),
    splitParent: z
        .object({
            cashItem: transactionCashItemReferenceSchema.nullable().optional(),
        })
        .nullable()
        .optional(),
    deletedAt: z.coerce.date().nullable().optional(),
});

// Transaction list item schema
export const transactionListItemSchema = transactionItemBaseSchema.extend({
    splits: z.array(transactionItemBaseSchema.partial()).nullable().optional(),
});

export type TransactionListItem = z.infer<typeof transactionListItemSchema>;

const bulkUpdateTransactionItemSchema = transactionListItemSchema.extend({
    entityId: z.number(),
});

export const bulkUpdateTransactionsResponseSchema = z.object({
    transactionsBeforeUpdate: z.array(bulkUpdateTransactionItemSchema),
    updatedTransactions: z.array(bulkUpdateTransactionItemSchema),
    canonicalNormalizedDescriptionId: z.string().uuid().nullable(),
});
export type BulkUpdateTransactionsResponse = z.infer<
    typeof bulkUpdateTransactionsResponseSchema
>;

export const bulkUpdateTransactionsPartialFailureSchema = z.object({
    message: z.string(),
    failedCount: z.number().int().nonnegative(),
    succeededCount: z.number().int().nonnegative(),
});
export type BulkUpdateTransactionsPartialFailure = z.infer<
    typeof bulkUpdateTransactionsPartialFailureSchema
>;

// Get transactions response schema
export const getTransactionsResponseSchema = z.object({
    data: z.array(transactionListItemSchema),
    total: z.number().optional(),
    pageCount: z.number().optional(),
});

export const getTransactionsResponseWithCountSchema =
    getTransactionsResponseSchema.extend({
        total: z.number(),
        pageCount: z.number(),
    });

export type GetTransactionsResponse = z.infer<
    typeof getTransactionsResponseSchema
>;

export type GetTransactionsResponseWithCount = z.infer<
    typeof getTransactionsResponseWithCountSchema
>;

export const getTransactionsSnapshotResponseSchema = z.object({
    ids: z.array(z.number()),
    isTruncated: z.boolean(),
    data: z.array(transactionListItemSchema),
});

export type GetTransactionsSnapshotResponse = z.infer<
    typeof getTransactionsSnapshotResponseSchema
>;
